/**
 * Energy Advisor data-source helpers. Centralize the API base path and nav-path
 * builder for the authenticated Energy Advisor workspace so every hook and
 * navigation helper goes through one place.
 *
 * (Historical: this module previously branched between an authenticated
 * surface and a public share viewer; the public viewer was removed as a
 * security-risk reduction. The data-source hook returns the
 * authenticated shape unconditionally.)
 */

import type { QueryClient } from "@tanstack/react-query";

export interface EnergyAdvisorDataSource {
  endpoint(path: string): string;
  queryKeyPrefix: readonly string[];
}

/**
 * The query-key prefix every customer-scoped Energy Advisor read lives under.
 * Exported so key helpers and invalidations build from the SAME prefix the
 * queries use — a hand-written copy (`["energy-advisor", "rfq", …]`) once left
 * every RFQ invalidation silently matching nothing (AB#8669).
 */
export function energyAdvisorCustomerKey(customerId: string | undefined) {
  return ["energy-advisor", "authed", customerId ?? "no-id"] as const;
}

/**
 * Refetch the Energy Advisor views of a customer after a write they read from
 * — e.g. a contract sign date, which the RFQ charts mark (AB#8669). Without a
 * customer in scope, sweeps every Energy Advisor query instead.
 */
export function invalidateEnergyAdvisorCustomer(
  qc: QueryClient,
  customerId: string | undefined,
) {
  qc.invalidateQueries({
    queryKey: customerId ? energyAdvisorCustomerKey(customerId) : ["energy-advisor"],
  });
}

export function useEnergyAdvisorDataSource(
  customerId: string | undefined,
): EnergyAdvisorDataSource {
  return {
    endpoint: (path) => `/api/energy-advisor/customers/${customerId}${path}`,
    queryKeyPrefix: energyAdvisorCustomerKey(customerId),
  };
}

/**
 * Resolves an in-app navigation path for an energy-advisor-customer surface.
 * Components inside the Energy Advisor subtree (snapshot tab's RFQ widget,
 * RFQ-list row click, walkthrough back link) use this so navigation
 * stays consistent.
 */
export function useEnergyAdvisorNavPath(customerId: string): {
  rfq: (rfqId: string) => string;
  rfqs: () => string;
  bills: (analysisId?: string) => string;
  snapshot: () => string;
} {
  const base = `/energy-advisor/${customerId}`;
  return {
    rfq: (rfqId) => `${base}/rfqs/${rfqId}`,
    rfqs: () => `${base}/rfqs`,
    bills: (analysisId) =>
      analysisId ? `${base}/bills?analysis=${analysisId}` : `${base}/bills`,
    snapshot: () => `${base}/snapshot`,
  };
}
